import { eq, object } from '@art-suite/art-core-ts';
import { getScanPartFromScanDataInMemoryWithoutChildren, ScanDataInMemory } from '../ScanDataInMemory';
import { SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH, ScanPart } from '../ScanRoot';
import { getChildTemplate } from '../ScanTemplateRoot';
import { getOptimizedScanPart } from './getOptimizedScan';
import { getScanPartsByScanPartId } from './ScanOptimizedGetters';
import { ScanPartOptimized, ScanRootOptimized } from './ScanRootOptimizedSchemas';
import { transformScanRootOptimized } from './transformScanRootOptimized';

export const getScanRootWithScanDataUpdates = (scanRoot: ScanRootOptimized, scanDataUpdates: ScanDataInMemory[]): ScanRootOptimized => {
  const scanDataUpdatesByScanPartId = object(scanDataUpdates, {
    withKey: ({ scanPartId }) => scanPartId,
  });
  const originalScanPartsByScanPartId = getScanPartsByScanPartId(scanRoot, 0);

  return transformScanRootOptimized(
    scanRoot,
    (scanPart, depth, idsByDepth, parentScope) => {
      const scanDataUpdate = scanDataUpdatesByScanPartId[scanPart.id];

      // No scan data update for this scan part - return unchanged
      if (!scanDataUpdate) return scanPart;

      // Apply scan data update
      const newScanPart = getScanPartFromScanDataInMemoryWithoutChildren(scanDataUpdate);

      // Ensure structural identity for unchanged values
      for (const key in newScanPart) {
        const typedKey = key as keyof ScanPart;
        if (eq(newScanPart[typedKey], scanPart[typedKey])) {
          (newScanPart as any)[typedKey] = scanPart[typedKey];
        }
      }

      // Handle children list updates if present
      const template = scanPart.template;
      const childrenFieldName = SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH[depth];
      if (scanDataUpdate.childrenScanPartIds && scanDataUpdate.childrenScanPartIds.length > 0 && childrenFieldName) {
        const scopeForChildren = getOptimizedScanPart(
          scanRoot.template,
          template,
          newScanPart,
          depth,
          idsByDepth,
          parentScope,
        ).optimized.optimizedValueExpressions.resolved;

        const children: ScanPartOptimized[] = [];
        for (const childId of scanDataUpdate.childrenScanPartIds) {
          // Reuse existing child if available
          let child = originalScanPartsByScanPartId[childId];

          // Create new child if not found
          if (!child) {
            const childUpdate = scanDataUpdatesByScanPartId[childId];
            if (!childUpdate || !childUpdate.templateScanPartId) continue;

            idsByDepth[depth + 1] = childId;
            child = getOptimizedScanPart(
              scanRoot.template,
              getChildTemplate(scanRoot.template, template, depth, childUpdate.templateScanPartId),
              getScanPartFromScanDataInMemoryWithoutChildren(childUpdate),
              depth + 1,
              idsByDepth,
              scopeForChildren,
            );
            idsByDepth[depth + 1] = undefined;
          }
          children.push(child);
        }
        (newScanPart as any)[childrenFieldName] = children;
      }

      // Return fully optimized scan part with updates
      return getOptimizedScanPart(
        scanRoot.template,
        template,
        newScanPart,
        depth,
        idsByDepth,
        parentScope,
        scanPart,
      );
    },
  );
};
